import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { getText } from '../../internal/TextLabels'
import { DataTable } from '../../midasComponents'
import { TransactionStatus } from '../../util/transactionConstants'
import {
  debit,
  disableCheckbox,
} from '../Transactions/shared/transaction-utils'
import { calculateLinkExpirationDate } from '../../util/dateUtils'
import resend from '../../assets/img/resend.svg'
import CurrencyFormat from '../../components/common/CurrencyFormat'
import { classNames } from '../../util/helpers'
import { debitDateHasPassed } from './ongoingPaymentUtils'
import OngoingPaymentsResendModal from './OngoingPaymentsResendSigningLinkModal'

import style from '../Transactions/shared/transactions.style'
import styles from './OngoingPayments.module.scss'
import { usePagination } from '../../provider/PaginationProvider'

const isBooleanAndFalse = value => typeof value === 'boolean' && value === false

const OngoingPaymentsTable = props => {
  const { paginationData } = usePagination()
  const [showModal, setShowModal] = useState(false)
  const [selectedRow, setSelectedRow] = useState('')
  const selectedTransaction =
    paginationData?.results?.find(
      transaction => transaction.id === selectedRow,
    ) ?? null
  const [signingLinkInfo, setSigningLinkInfo] = useState({
    email: selectedTransaction?.customer?.email,
    mobile: selectedTransaction?.customer?.mobile,
    valid: false,
  })

  const getUserName = id => {
    const foundUser = props.users.byHash[id]
    return foundUser && `${foundUser.firstName} ${foundUser.lastName}`
  }

  const handleClick = transaction => {
    props.nextRoute(`${transaction.id}/details`)
  }

  const renderStatusArea = (
    transactionStatus,
    transactionId,
    isAutomaticDebit,
    isDelayedPayment,
    hasDebitFailed,
    automaticDebitDate,
  ) => {
    if (hasDebitFailed) {
      return (
        <div className={styles.container}>
          <div
            className={styles.failedDebitText}
            title={getText('ongoingPayments.status.failed')}
          >
            {getText('ongoingPayments.status.failed')}
          </div>
        </div>
      )
    }
    if (
      transactionStatus === TransactionStatus.SIGNING_REQUIRED ||
      transactionStatus === TransactionStatus.CUSTOMER_INTERACTION_REQUIRED
    ) {
      return (
        <div className={styles.notSignedContainer}>
          <div
            title={getText('ongoingPayments.status.notSigned')}
            className={
              debitDateHasPassed(automaticDebitDate)
                ? styles.notSignedAndDateHasPassed
                : styles.notSignedText
            }
          >
            {getText('ongoingPayments.status.notSigned')}
          </div>
          {isBooleanAndFalse(isAutomaticDebit) && !!isDelayedPayment && (
            <div
              title={getText('ongoingPayments.resend')}
              role="button"
              aria-hidden
              className={styles.icon}
              onClick={event => {
                event.stopPropagation()
                setSelectedRow(transactionId)
                setShowModal(true)
              }}
            >
              <img alt={getText('ongoingPayments.resend')} src={resend} />
            </div>
          )}
        </div>
      )
    }
    return (
      <div className={styles.container}>
        <div
          className={styles.debitableText}
          title={getText('ongoingPayments.status.debitable')}
        >
          {getText('ongoingPayments.status.debitable')}
        </div>
      </div>
    )
  }

  const renderTableBody = () => {
    let tableBody = []
    if (paginationData.results && paginationData.results.length > 0) {
      tableBody = paginationData.results.map(transaction => (
        <tr
          className={styles.cursor}
          onClick={() => handleClick(transaction)}
          data-checkbox-disabled={
            disableCheckbox(transaction, props.hasInvoiceNumber, {})
              ? 'true'
              : 'false'
          }
          key={transaction.id || Math.random()}
        >
          <td>
            {renderStatusArea(
              transaction.status,
              transaction.id,
              transaction.publicSettings?.automaticDebit,
              transaction.payment?.settings?.delayedPayment,
              transaction.debitFailed,
              transaction.payment?.settings?.automaticDebitDate,
            )}
          </td>
          <td style={{ height: '55px' }}>
            {transaction.customer &&
              transaction.customer.customerGivenAddress &&
              transaction.customer.customerGivenAddress.name.full}
          </td>

          <td>
            {transaction.store &&
              transaction.store.salesPersonId &&
              getUserName(transaction.store.salesPersonId)}
          </td>
          <td>
            {transaction.paymentMethod &&
              (transaction.paymentMethod.provider &&
              transaction.paymentMethod.provider.toLowerCase() !== 'resurs'
                ? transaction.paymentMethod.provider
                : transaction.paymentMethod.description)}
          </td>

          <td style={style.tableNumber}>
            <CurrencyFormat
              value={transaction?.payment?.paymentStatus?.totalOrderAmount}
            />
            <CurrencyFormat value={transaction?.amount} />
          </td>
          {props.delayedPaymentAutomaticDebitDateEnabled &&
            (transaction.publicSettings?.pushedOutToCustomer &&
            transaction.publicSettings?.automaticDebit &&
            (transaction.status === TransactionStatus.SIGNING_REQUIRED ||
              transaction.status ===
                TransactionStatus.CUSTOMER_INTERACTION_REQUIRED) ? (
              <td style={style.tableDate}>
                {getText('ongoingPayments.whenSigned')}
              </td>
            ) : (
              <td
                style={style.tableDate}
                className={classNames(
                  debitDateHasPassed(
                    transaction.payment.settings.automaticDebitDate,
                  ) && styles.strikeThrough,
                )}
              >
                {transaction.payment.settings.automaticDebitDate}
              </td>
            ))}
          <td style={style.tableDate}>
            {transaction.status === 'SIGNING_REQUIRED' ||
            transaction.status === 'CUSTOMER_INTERACTION_REQUIRED'
              ? calculateLinkExpirationDate(transaction.date)
              : getText('ongoingPayments.status.signed')}
          </td>
        </tr>
      ))
    }
    return tableBody
  }

  return (
    <>
      <DataTable
        hover={false}
        striped={false}
        wrap
        fixed
        noContentMsg={getText('ongoingPayments.noContentMessage')}
        searchTerm={paginationData.searchQuery}
        searchTitle={getText('default.search')}
        rowsPerPageTitle={getText('default.rowsPerPage')}
        actionButton={{
          label: getText('transactions.action.options.debitAll'),
          onClick: items => {
            debit(
              items,
              paginationData.results,
              props.hasInvoiceNumber,
              {},
              props.debit,
            )
          },
          loading: props.debitLoading,
          callbackOnClear: () => props.loadTransactions(),
        }}
        pagination={{
          currentPage: paginationData.page + 1,
          totalPages: paginationData.totalPages,
          rowsPerPage: paginationData.size,
          totalCount: paginationData.totalElements,
        }}
        onNext={props.onNext}
        onPrev={props.onPrev}
        onRowsPerPageChange={size => props.onChangeSize(size)}
        lastSort={
          paginationData.sort
            ? {
                headerName: paginationData.sort.split(',')[0],
                direction: paginationData.sort.split(',')[1],
              }
            : null
        }
        onSort={(currentHeaderId, direction) => {
          props.onChangeSortPriority(currentHeaderId, direction)
        }}
        onSearch={searchTerm => props.onLoadSearchData(searchTerm)}
        search
        placeholder={getText('ongoingPayments.searchPlaceholder')}
        headers={props.headers}
        dataCy="ongoing-transaction-list"
        fixedActionBar
      >
        <tbody>{renderTableBody()}</tbody>
      </DataTable>
      {showModal && (
        <OngoingPaymentsResendModal
          selectedTransaction={selectedTransaction}
          signingLinkInfo={signingLinkInfo}
          setSigningLinkInfo={setSigningLinkInfo}
          setShowModal={setShowModal}
        />
      )}
    </>
  )
}

OngoingPaymentsTable.propTypes = {
  onChangeSortPriority: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onChangeSize: PropTypes.func.isRequired,
  onLoadSearchData: PropTypes.func.isRequired,
  debit: PropTypes.func.isRequired,
  debitLoading: PropTypes.bool.isRequired,
  loadTransactions: PropTypes.func.isRequired,
  hasInvoiceNumber: PropTypes.bool.isRequired,
  users: PropTypes.object.isRequired,
  delayedPaymentAutomaticDebitDateEnabled: PropTypes.bool.isRequired,
  nextRoute: PropTypes.func.isRequired,
}

export default OngoingPaymentsTable
